// features/workstations/qqq/prediction/hybrid-panel.js
// ═══════════════════════════════════════════════════════════
// HYBRID TRADE CARD — the live cluster-rules + agent ensemble decision.
//
// Calls /api/qqq/hybrid-predict, displays the trade card with:
//   - 10:30 ET agent signal (size modulator)
//   - 11:30 ET cluster-rules signal (trade signal)
//   - Combined hybrid decision (with vehicle + sizing + exit rules)
//
// Backtested: 30 trades / 99 days, 50% win rate, ~$190/trade after slippage
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

function todayISO() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

function DirectionBadge({ direction, conviction }) {
  const colors = {
    up: '#7a9e82',     // sage green
    down: '#c46050',   // terracotta
    chop: '#9b8fc4',   // purple
  };
  const color = colors[direction?.toLowerCase()] || '#888';
  return (
    <span style={{
      display: 'inline-block', padding: '4px 12px',
      background: `${color}22`, color, borderRadius: 4,
      fontSize: 13, fontWeight: 700, letterSpacing: '0.05em',
    }}>
      {(direction || 'SKIP').toUpperCase()}{conviction ? ` · ${conviction}` : ''}
    </span>
  );
}

export default function HybridPanel({ collapsed = false }) {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(!collapsed);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/qqq/hybrid-predict?date=${date}`);
      const json = await res.json();
      if (json.error && !json.hybrid_decision && !json.agent_signal) {
        setError(json.message || json.error);
        setData(json);
      } else {
        setData(json);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [date]);

  // Auto-run on mount if today
  useEffect(() => {
    if (open && date === todayISO()) run();
  }, [open, date, run]);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="qqq-hybrid-toggle">
        ▶ Open Hybrid Trade Card
      </button>
    );
  }

  return (
    <div className="qqq-hybrid">
      <div className="qqq-hybrid__header">
        <div>
          <h3>Hybrid Trade Card</h3>
          <div className="qqq-hybrid__sub">Cluster-rules + Agent ensemble · backtested $190/trade after slippage</div>
        </div>
        <div className="qqq-hybrid__controls">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="qqq-hybrid__date"
          />
          <button onClick={run} disabled={loading} className="qqq-hybrid__run">
            {loading ? 'Running…' : 'Run'}
          </button>
        </div>
      </div>

      <div className="qqq-hybrid__schedule">
        <div className="qqq-hybrid__schedule-title">⏱ When to run today</div>
        <ol>
          <li><strong>10:30 ET</strong> — hit <strong>Run</strong> → Agent signal appears (size modulator)</li>
          <li><strong>11:30 ET</strong> — hit <strong>Run</strong> again → Cluster-rules + full trade decision</li>
          <li><strong>11:45 ET</strong> — check sequence; if window 9 = C16 AND you're long UP → exit</li>
          <li><strong>During day</strong> — watch QQQ for profit-take target shown on the card</li>
          <li><strong>15:55 ET</strong> — close any open position before MOC if not already triggered</li>
        </ol>
        <div className="qqq-hybrid__schedule-note">
          Running before 10:30 ET = not enough cluster windows formed yet.
          The system fires on ~30% of days; most days you'll see <em>NO TRADE</em>.
        </div>
      </div>

      {error && (
        <div className="qqq-hybrid__error">⚠ {error}</div>
      )}

      {data && !data.error && (
        <div className="qqq-hybrid__body">
          {/* Status row */}
          <div className="qqq-hybrid__status">
            <span>Date: <strong>{data.date}</strong></span>
            <span>Windows: <strong>{data.completed_windows}/{data.total_windows}</strong></span>
            <span>Sequence: <code>{(data.sequence || []).filter((c) => c != null).join('→') || '—'}</code></span>
          </div>

          {/* Agent signal */}
          <div className="qqq-hybrid__section">
            <div className="qqq-hybrid__section-title">10:30 ET Agent Signal · size modulator</div>
            {data.agent_signal ? (
              <div className="qqq-hybrid__signal">
                <DirectionBadge direction={data.agent_signal.top1} />
                <div className="qqq-hybrid__conf">
                  Conf: <strong>{(data.agent_signal.conf * 100).toFixed(1)}%</strong>
                  &nbsp;·&nbsp;
                  up={(data.agent_signal.probs.up * 100).toFixed(0)}%,
                  down={(data.agent_signal.probs.down * 100).toFixed(0)}%,
                  chop={(data.agent_signal.probs.chop * 100).toFixed(0)}%
                </div>
              </div>
            ) : (
              <div className="qqq-hybrid__waiting">Waiting for 10:30 ET (need 4 windows complete)</div>
            )}
          </div>

          {/* Cluster-rules signal */}
          <div className="qqq-hybrid__section">
            <div className="qqq-hybrid__section-title">11:30 ET Cluster-Rules Signal · trade signal</div>
            {data.cluster_rules_signal ? (
              <div className="qqq-hybrid__signal">
                <DirectionBadge direction={data.cluster_rules_signal.direction} conviction={data.cluster_rules_signal.conviction} />
                <div className="qqq-hybrid__conf">
                  Rule: <code>{data.cluster_rules_signal.rule}</code>
                  &nbsp;(N={data.cluster_rules_signal.rule_N}, conf={(data.cluster_rules_signal.rule_conf * 100).toFixed(0)}%)
                </div>
              </div>
            ) : (
              <div className="qqq-hybrid__waiting">
                {data.completed_windows < 8
                  ? `Waiting for 11:30 ET (have ${data.completed_windows}/8 windows)`
                  : 'No rule fired at HIGH or MED-HIGH conviction. NO TRADE.'}
              </div>
            )}
          </div>

          {/* Hybrid decision */}
          {data.hybrid_decision ? (
            <div className="qqq-hybrid__decision">
              <div className="qqq-hybrid__decision-header">
                <DirectionBadge direction={data.hybrid_decision.direction} conviction={data.hybrid_decision.conviction} />
                <span className="qqq-hybrid__size">
                  Size: <strong>{data.hybrid_decision.final_size}x</strong>
                  {data.hybrid_decision.agent_agrees ? ' (agent confirms ✓)' : ' (agent neutral)'}
                </span>
              </div>
              <div className="qqq-hybrid__trade">
                <div className="qqq-hybrid__row">
                  <span className="qqq-hybrid__label">Vehicle</span>
                  <span className="qqq-hybrid__value">{data.hybrid_decision.vehicle}</span>
                </div>
                <div className="qqq-hybrid__row">
                  <span className="qqq-hybrid__label">Entry</span>
                  <span className="qqq-hybrid__value">
                    {data.hybrid_decision.entry_time} @ QQQ ${data.hybrid_decision.entry_price}
                  </span>
                </div>
                <div className="qqq-hybrid__row">
                  <span className="qqq-hybrid__label">{data.hybrid_decision.direction === 'up' ? 'Buy call' : 'Buy put'}</span>
                  <span className="qqq-hybrid__value">strike ${data.hybrid_decision.strike} (ATM)</span>
                </div>
                <div className="qqq-hybrid__row">
                  <span className="qqq-hybrid__label">{data.hybrid_decision.direction === 'up' ? 'Sell call' : 'Sell put'}</span>
                  <span className="qqq-hybrid__value">
                    strike ${data.hybrid_decision.otm_strike}
                    ({data.hybrid_decision.direction === 'up' ? '+' : '-'}${data.hybrid_decision.spread_width} OTM)
                  </span>
                </div>
                <div className="qqq-hybrid__row qqq-hybrid__row--accent">
                  <span className="qqq-hybrid__label">Profit-take</span>
                  <span className="qqq-hybrid__value">QQQ ${data.hybrid_decision.profit_take_price}</span>
                </div>
              </div>

              <div className="qqq-hybrid__exits">
                <div className="qqq-hybrid__section-title">Exit rules</div>
                <ol>
                  <li><strong>11:45 ET cluster check (C16-exit)</strong>: {data.hybrid_decision.exit_rules.c16_exit}</li>
                  <li><strong>Profit-take (X1)</strong>: {data.hybrid_decision.exit_rules.x1_profit_take}</li>
                  <li><strong>Default</strong>: {data.hybrid_decision.exit_rules.default}</li>
                </ol>
              </div>
            </div>
          ) : (
            data.cluster_rules_signal && data.cluster_rules_signal.direction === 'chop' ? (
              <div className="qqq-hybrid__skip">⏸ Cluster called CHOP — NO TRADE today</div>
            ) : data.completed_windows >= 8 ? (
              <div className="qqq-hybrid__skip">⏸ NO TRADE today (no HIGH/MED-HIGH rule fired)</div>
            ) : null
          )}
        </div>
      )}

      <style jsx>{`
        .qqq-hybrid-toggle {
          margin: 16px 0; padding: 8px 14px;
          background: #111118; color: #c8a96e;
          border: 1px solid #c8a96e66; border-radius: 6px;
          font-size: 13px; cursor: pointer;
        }
        .qqq-hybrid {
          margin: 16px 0; padding: 16px;
          background: #07070a; border: 1px solid #272736; border-radius: 8px;
          color: #ccc; font-size: 13px; line-height: 1.5;
        }
        .qqq-hybrid__header {
          display: flex; align-items: flex-start; justify-content: space-between;
          margin-bottom: 16px; padding-bottom: 12px;
          border-bottom: 1px solid #272736;
        }
        .qqq-hybrid__header h3 { margin: 0 0 4px; color: #c8a96e; font-size: 16px; font-weight: 600; }
        .qqq-hybrid__sub { color: #888; font-size: 11px; }
        .qqq-hybrid__controls { display: flex; gap: 8px; }
        .qqq-hybrid__date {
          background: #171723; color: #ccc; border: 1px solid #3a3a52;
          padding: 4px 8px; border-radius: 4px; font-size: 12px;
        }
        .qqq-hybrid__run {
          background: #c8a96e; color: #07070a; border: none;
          padding: 4px 14px; border-radius: 4px; cursor: pointer;
          font-size: 12px; font-weight: 600;
        }
        .qqq-hybrid__run:disabled { opacity: 0.5; cursor: wait; }
        .qqq-hybrid__schedule {
          margin: 0 0 16px;
          padding: 12px 14px;
          background: #111118;
          border: 1px solid #272736;
          border-left: 3px solid #c8a96e;
          border-radius: 4px;
        }
        .qqq-hybrid__schedule-title {
          color: #c8a96e;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 8px;
        }
        .qqq-hybrid__schedule ol {
          margin: 0 0 8px;
          padding-left: 22px;
        }
        .qqq-hybrid__schedule li {
          margin: 3px 0;
          color: #aaa;
          font-size: 12px;
          line-height: 1.5;
        }
        .qqq-hybrid__schedule strong {
          color: #c8a96e;
        }
        .qqq-hybrid__schedule-note {
          color: #888;
          font-size: 11px;
          font-style: italic;
          padding-top: 6px;
          border-top: 1px solid #1e1e2a;
          margin-top: 4px;
        }
        .qqq-hybrid__error {
          padding: 10px; background: #c4605022; color: #c46050;
          border-radius: 4px; margin-bottom: 12px;
        }
        .qqq-hybrid__status {
          display: flex; gap: 18px; flex-wrap: wrap;
          margin-bottom: 16px; font-size: 11px; color: #888;
        }
        .qqq-hybrid__status code {
          background: #171723; padding: 2px 6px; border-radius: 3px;
          color: #c8a96e; font-size: 10px;
        }
        .qqq-hybrid__section {
          margin: 12px 0; padding: 12px;
          background: #111118; border: 1px solid #1e1e2a; border-radius: 6px;
        }
        .qqq-hybrid__section-title {
          color: #c8a96e; font-size: 11px; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;
        }
        .qqq-hybrid__signal { display: flex; align-items: center; gap: 14px; }
        .qqq-hybrid__conf { font-size: 12px; color: #aaa; }
        .qqq-hybrid__conf code {
          background: #171723; padding: 2px 6px; border-radius: 3px;
          color: #b8956a; font-size: 11px;
        }
        .qqq-hybrid__waiting { color: #888; font-style: italic; font-size: 12px; }
        .qqq-hybrid__decision {
          margin: 16px 0; padding: 16px;
          background: linear-gradient(180deg, #c8a96e11, transparent);
          border: 2px solid #c8a96e; border-radius: 8px;
        }
        .qqq-hybrid__decision-header {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 16px; padding-bottom: 12px;
          border-bottom: 1px solid #272736;
        }
        .qqq-hybrid__size { color: #c8a96e; font-size: 13px; }
        .qqq-hybrid__trade {
          display: grid; grid-template-columns: 1fr 1fr; gap: 4px 18px;
          margin-bottom: 16px;
        }
        .qqq-hybrid__row {
          display: flex; justify-content: space-between; padding: 4px 0;
          border-bottom: 1px solid #1e1e2a;
        }
        .qqq-hybrid__row--accent {
          background: #c8a96e11; padding: 6px 8px; border-radius: 4px;
          border-bottom: none;
        }
        .qqq-hybrid__label { color: #888; font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; }
        .qqq-hybrid__value { color: #ccc; font-size: 12px; }
        .qqq-hybrid__exits {
          margin-top: 16px; padding: 12px;
          background: #171723; border-radius: 6px;
        }
        .qqq-hybrid__exits ol { margin: 0; padding-left: 20px; }
        .qqq-hybrid__exits li { margin: 4px 0; color: #aaa; font-size: 11px; }
        .qqq-hybrid__exits strong { color: #c8a96e; }
        .qqq-hybrid__skip {
          margin: 16px 0; padding: 14px;
          background: #888122; color: #aaa; border-radius: 6px;
          text-align: center; font-size: 13px;
        }
      `}</style>
    </div>
  );
}
